import { Help } from "./Help";
import { DisplayModeToggle } from "./Appearance";
import "./accounts.css";
import { useEffect, useState } from "react";
import { EsmAccessContext } from "./FeatureAccess";
import { accountRequest, type AccountView } from "./account-api";

export function PublicDocumentation() {
  const [esmAllowed, setEsmAllowed] = useState(false);
  useEffect(() => {
    let active = true;
    const load = () => {
      void accountRequest<AccountView>("/status")
        .then((view) => {
          if (active) setEsmAllowed(view.features?.esm === true);
        })
        .catch(() => {
          if (active) setEsmAllowed(false);
        });
    };
    load();
    const timer = setInterval(load, 5000);
    window.addEventListener("focus", load);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("focus", load);
    };
  }, []);
  const integration = location.pathname.startsWith("/docs");
  return (
    <main className="public-documentation">
      <header className="public-documentation-header">
        <a href="/help">
          <strong>Proof Studio</strong> · Documentation
        </a>
        <nav aria-label="Documentation">
          <a href="/help">Guides & help</a>
          <a href="/docs">API integration</a>
          <a href="/docs/scalar">Scalar</a>
          <a href="/login">Sign in</a>
        </nav>
        <DisplayModeToggle />
      </header>
      {integration ? (
        <article className="public-integration-guide">
          <span className="eyebrow">DEVELOPER GUIDE</span>
          <h1>Integrate with Proof Studio</h1>
          <p>
            Connect your application to projects, forms, document proofs and
            output generation. These guides and API definitions are public;
            access to workspace data requires authentication.
          </p>
          <p>
            <a href="/docs/scalar">Browse the API in Scalar</a> ·{" "}
            <a href="/docs/openapi.json">Workspace OpenAPI JSON</a> ·{" "}
            <a href="/docs/integration-openapi.json">
              Authentication OpenAPI JSON
            </a>
          </p>
          <h2>1. Create an integration client</h2>
          <p>
            Sign in as an administrator, select the site and company, then open
            Administration → Integrations. Create a client for the intended
            workspace, choose its allowed capabilities, token lifetime and
            allowed browser origins. Keep the client secret on your server.
          </p>
          <h2>2. Request an access token on your server</h2>
          <p>
            Exchange client credentials at <code>POST /api/oauth/token</code>.
            Request only the scopes your application needs. Tokens are bound to
            the client's workspace and expire; there are no refresh tokens.
          </p>
          <pre>{String.raw`curl -X POST https://YOUR-PROOF-HOST/api/oauth/token \
  -u "$PROOF_CLIENT_ID:$PROOF_CLIENT_SECRET" \
  -H 'Content-Type: application/json' \
  -d '{"grant_type":"client_credentials","scope":"tenant.documents.view","expires_in":900}'`}</pre>
          <h2>3. Call the workspace API</h2>
          <p>
            Use the returned <code>access_token</code> in the Authorization
            header. The API checks the token, workspace, capabilities and
            resource permissions for every operation. See Scalar for the exact
            request and response schemas, pagination and errors.
          </p>
          <pre>{String.raw`curl https://YOUR-PROOF-HOST/api/projects?projection=summary \
  -H "Authorization: Bearer $PROOF_ACCESS_TOKEN"`}</pre>
          <h2>4. Embed a form or project editor</h2>
          <p>
            Open Configuration → Integrations in the workspace to generate host
            code for a proof form, project designer, fields and variables, or a
            configurable popout. The host obtains short-lived tokens through its
            own backend; client secrets must never appear in browser code.
          </p>
          <p>
            Use <code>proof-form</code> for a customer-facing form,{" "}
            <code>proof-authoring</code> for the permitted editor views, and{" "}
            <code>proof-integration-modal</code> to show either in a modal.
            Connect host persistence to change events; closing the popout
            preserves mounted values but does not replace your save workflow.
          </p>
          {esmAllowed && (
            <>
              <h2>Install the .NET NuGet package</h2>
              <p>
                Your account can download <strong>ProofStudio.Esm 0.1.9</strong>{" "}
                for .NET 8 and later. It converts ESM DataSet tables to the
                project editor's JSON and restores edited tables and types. Your
                host remains responsible for database access and saving changes.
                The package also publishes the complete browser SDK and designer
                runtime assets into your ASP.NET application's wwwroot.
              </p>
              <p>
                <a href="/nuget/v3/index.json">NuGet V3 feed</a> ·{" "}
                <a href="/downloads/ProofStudio.Esm.0.1.9.nupkg">
                  Download the package
                </a>
              </p>
              <p>
                Download the package into a local packages folder while signed
                in, then install it from that folder. Package discovery and
                downloads require an eligible account session.
              </p>
              <pre>{`dotnet add package ProofStudio.Esm --version 0.1.9 --source ./packages`}</pre>
              <p>
                The packaged README includes ASP.NET static-file configuration,
                including the extensionless model data files used by the
                designer.
              </p>
              <pre>{`// Enable static files in the published ASP.NET host:
app.UseStaticFiles();
// Load the editor from your own site:
// <script type="module" src="/proof-studio/plugins/esm-project.mjs"></script>`}</pre>
              <pre>{`using ProofStudio.Esm;
string inputJson = EsmJson.ToJson(esmDataSet);
// Send inputJson to the proof-esm-project editor.
EsmDocument edited = EsmJson.FromJson(returnedJson);
// Validate and persist edited.DataSet with your own database layer.`}</pre>
            </>
          )}
          <h2>Errors and delivery troubleshooting</h2>
          <p>
            Handle non-success HTTP responses and expired tokens explicitly.
            Grant only capabilities your client needs. For email tests,
            administrators can inspect Email providers → Email delivery logs for
            provider responses and delivery references.
          </p>
          <p>
            <a href="/help">Read all product help guides</a>
          </p>
        </article>
      ) : (
        <EsmAccessContext.Provider value={esmAllowed}>
          <Help
            onNavigate={() => {
              location.assign("/login");
            }}
          />
        </EsmAccessContext.Provider>
      )}
    </main>
  );
}
